import { useRef, useEffect } from 'react';
import { usePhone, type PhoneAction } from '@/state/phone';
import { useDialogue, pickResponseIdx, type FollowUpRef } from '@/state/dialogue';
import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { useWallet } from '@/state/wallet';
import { getDialogueTree } from '@/content/dialogue';
import type { DialogueChoice, DialogueResponse } from '@/content/dialogue/types';
import { applyChoiceEffects } from '@/engine/dialogueEffects';
import { cn } from '@/lib/cn';
import { formatRelativeTime } from '@/lib/time';
import { invokePhoneAction } from '@/engine/phoneActions';
import { sounds } from '@/audio/synth';

export function Conversation({ threadId }: { threadId: string }) {
  const contacts = usePhone((s) => s.contacts);
  const messages = usePhone((s) => s.messages);
  const close = usePhone((s) => s.closeThread);
  const sendMsg = usePhone((s) => s.sendMessage);
  const receiveMessage = usePhone((s) => s.receiveMessage);
  const markActionTaken = usePhone((s) => s.markActionTaken);

  const followUpsByThread = useDialogue((s) => s.followUpsByThread);
  const firedPairs = useDialogue((s) => s.firedPairs);
  const usedOneShots = useDialogue((s) => s.usedOneShots);
  const pendingReplyByThread = useDialogue((s) => s.pendingReplyByThread);
  const setFollowUps = useDialogue((s) => s.setFollowUps);
  const recordFired = useDialogue((s) => s.recordFired);
  const recordOneShot = useDialogue((s) => s.recordOneShot);
  const setPendingReply = useDialogue((s) => s.setPendingReply);

  const contact = contacts[threadId];
  const threadMsgs = messages.filter((m) => m.threadId === threadId);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [threadMsgs.length]);

  if (!contact) return null;

  const tree = getDialogueTree(threadId);
  const fuRef = followUpsByThread[threadId];
  const fired = firedPairs[threadId] ?? [];
  const oneShot = usedOneShots[threadId] ?? [];

  // Resolve which choices to show: follow-ups for the active node, or root.
  const activeChoices = resolveActiveChoices(tree, fuRef);

  const requireCtx = buildRequireCtx();
  const visibleChoices = (activeChoices ?? []).filter((c) => {
    if (oneShot.includes(c.id)) return false;
    if (c.requires && !c.requires(requireCtx)) return false;
    return true;
  });

  const handleChoice = (c: DialogueChoice) => {
    // 1. Player message lands immediately
    sendMsg(threadId, c.playerSays);
    // 2. Apply choice effects
    applyChoiceEffects(c.effects);
    if (c.oneShot) recordOneShot(threadId, c.id);
    sounds.notifyInfo();
    // 3. Pick a non-repeating response variant
    const idx = pickResponseIdx(c.id, c.responses.length, fired);
    const resp = c.responses[idx];
    if (!resp) return;
    recordFired(threadId, c.id, idx);
    // 4. Schedule NPC reply, mark thread as pending so the typing indicator shows
    const delayMs = 4_000 + Math.floor(Math.random() * 6_000);  // 4-10s
    setPendingReply(threadId, true);
    setTimeout(() => {
      receiveMessage({ threadId, fromNpc: threadId, text: resp.text });
      applyChoiceEffects(resp.effects);
      setPendingReply(threadId, false);
      // 5. After reply lands, set follow-ups (or clear → root)
      if (resp.followUps && resp.followUps.length > 0 && !resp.endsThread) {
        setFollowUps(threadId, { rootChoiceId: c.id, responseIdx: idx });
      } else {
        setFollowUps(threadId, undefined);
      }
    }, delayMs);
  };

  const handleBackToRoot = () => {
    setFollowUps(threadId, undefined);
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2 py-2">
        <button
          onClick={close}
          className="rounded p-1.5 text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]"
          aria-label="Back"
        >
          ←
        </button>
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--bg-elevated)] text-mono-sm font-bold text-[var(--text-primary)]">
          {contact.displayName.charAt(0)}
        </div>
        <span className="text-mono-md font-bold text-[var(--text-primary)]">
          {contact.displayName}
        </span>
      </header>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3">
        <div className="flex flex-col gap-2">
          {threadMsgs.map((m) => (
            <div
              key={m.id}
              className={cn(
                'flex flex-col',
                m.fromSelf ? 'items-end' : 'items-start',
              )}
            >
              <div
                className={cn(
                  'max-w-[80%] rounded-2xl px-3 py-1.5 text-mono-sm whitespace-pre-wrap',
                  m.fromSelf
                    ? 'bg-[var(--accent-primary)] text-black rounded-br-sm'
                    : 'bg-[var(--bg-elevated)] text-[var(--text-primary)] rounded-bl-sm',
                )}
              >
                {m.text}
              </div>
              {m.actions && m.actions.length > 0 && (
                <ActionRow
                  actions={m.actions}
                  takenIds={m.actionsTakenIds ?? []}
                  onAction={(a) => {
                    if ((m.actionsTakenIds ?? []).length > 0) return;
                    invokePhoneAction(a.effectId);
                    markActionTaken(m.id, a.id);
                  }}
                />
              )}
              <span className="mt-0.5 text-[10px] text-[var(--text-muted)]">
                {formatRelativeTime(m.timestamp)}
              </span>
            </div>
          ))}
          {pendingReplyByThread[threadId] && (
            <div className="flex items-start">
              <div className="rounded-2xl rounded-bl-sm bg-[var(--bg-elevated)] px-3 py-2 text-mono-sm text-[var(--text-muted)]">
                <span className="inline-flex gap-1">
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--text-muted)]" style={{ animationDelay: '0ms' }} />
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--text-muted)]" style={{ animationDelay: '160ms' }} />
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--text-muted)]" style={{ animationDelay: '320ms' }} />
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Quick-reply choices — replaces free-text input. Authored content; both sides written together. */}
      <div className="flex flex-col gap-1.5 border-t border-[var(--border-subtle)] bg-[var(--bg-surface)] p-2">
        {!tree && (
          <div className="text-mono-xs italic text-[var(--text-muted)] px-2 py-1">
            no dialogue tree authored for this contact yet — they go silent.
          </div>
        )}
        {tree && visibleChoices.length === 0 && (
          <div className="flex items-center justify-between gap-2 px-2 py-1">
            <span className="text-mono-xs italic text-[var(--text-muted)]">
              {fuRef ? 'waiting for reply…' : 'nothing left to say.'}
            </span>
            {fuRef && (
              <button
                onClick={handleBackToRoot}
                className="text-mono-xs text-[var(--accent-primary)] hover:underline"
              >
                back to topics
              </button>
            )}
          </div>
        )}
        {tree && visibleChoices.length > 0 && (
          <>
            {fuRef && (
              <button
                onClick={handleBackToRoot}
                className="self-start text-[10px] uppercase tracking-wider text-[var(--text-muted)] hover:text-[var(--accent-primary)]"
              >
                ← back to topics
              </button>
            )}
            <div className="flex flex-wrap gap-1.5">
              {visibleChoices.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleChoice(c)}
                  disabled={pendingReplyByThread[threadId]}
                  className={cn(
                    'rounded-full border px-3 py-1 text-mono-xs transition-colors',
                    pendingReplyByThread[threadId]
                      ? 'border-[var(--border-subtle)] bg-[var(--bg-deep)] text-[var(--text-muted)] opacity-40 cursor-not-allowed'
                      : c.destructive
                      ? 'border-[var(--negative)] bg-[var(--bg-deep)] text-[var(--negative)] hover:bg-[var(--negative)] hover:text-black'
                      : 'border-[var(--border-strong)] bg-[var(--bg-deep)] text-[var(--text-primary)] hover:bg-[var(--accent-primary)] hover:text-black',
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function resolveActiveChoices(
  tree: ReturnType<typeof getDialogueTree>,
  fu: FollowUpRef | undefined,
): DialogueChoice[] | undefined {
  if (!tree) return undefined;
  if (!fu) return tree.rootChoices;
  const root = tree.rootChoices.find((c) => c.id === fu.rootChoiceId);
  const resp: DialogueResponse | undefined = root?.responses[fu.responseIdx];
  return resp?.followUps;
}

function buildRequireCtx() {
  const cash = useMarkets.getState().cash;
  const life = useLife.getState();
  const wallet = useWallet.getState();
  return {
    cash,
    irsHeat: life.irsHeat,
    secHeat: life.secHeat,
    fbiHeat: life.fbiHeat,
    sanity: life.sanity,
    rel: life.relationships as Record<string, number>,
    hasOwnedJet: wallet.ownsJet,
    hasOwnedYacht: wallet.ownsYacht,
  };
}

function ActionRow({ actions, takenIds, onAction }: { actions: PhoneAction[]; takenIds: string[]; onAction: (a: PhoneAction) => void }) {
  const locked = takenIds.length > 0;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {actions.map((a) => {
        const taken = takenIds.includes(a.id);
        return (
          <button
            key={a.id}
            onClick={() => onAction(a)}
            disabled={locked}
            className={cn(
              'rounded-full border px-3 py-0.5 text-mono-xs transition-colors',
              taken
                ? 'border-[var(--accent-primary)] bg-[var(--bg-elevated)] text-[var(--accent-primary)]'
                : locked
                ? 'border-[var(--border-subtle)] text-[var(--text-muted)] opacity-30 cursor-not-allowed'
                : a.destructive
                ? 'border-[var(--negative)] text-[var(--negative)] hover:bg-[var(--negative)] hover:text-black'
                : 'border-[var(--border-strong)] text-[var(--text-primary)] hover:bg-[var(--accent-primary)] hover:text-black',
            )}
          >
            {taken ? `✓ ${a.label}` : a.label}
          </button>
        );
      })}
    </div>
  );
}
